import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar, TrendingUp, DollarSign, Target, Award } from 'lucide-react';
import { useAppointmentStats } from '@/hooks/useAppointmentStats';
import { useQuoteMetrics } from '@/hooks/useQuoteMetrics';
import { Skeleton } from '@/components/ui/skeleton';

interface SalesRepMetricsProps {
  userId: string;
}

export const SalesRepMetrics = ({ userId }: SalesRepMetricsProps) => {
  const { stats: appointmentStats, loading: appointmentsLoading } = useAppointmentStats(undefined, userId);
  const { metrics: quoteMetrics, loading: quotesLoading } = useQuoteMetrics(userId);

  const loading = appointmentsLoading || quotesLoading;

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        {[...Array(5)].map((_, i) => (
          <Card key={i}>
            <CardHeader className="pb-3">
              <Skeleton className="h-8 w-8" />
              <Skeleton className="h-4 w-24 mt-2" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-16" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const performanceRating = () => {
    const rate = appointmentStats.conversionRate;
    if (rate >= 70) return { label: 'Excellent', color: 'text-green-600' };
    if (rate >= 50) return { label: 'Good', color: 'text-blue-600' };
    if (rate >= 30) return { label: 'Fair', color: 'text-yellow-600' };
    return { label: 'Needs Improvement', color: 'text-destructive' };
  };

  const performance = performanceRating();

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
      <Card className="hover:border-accent transition-all">
        <CardHeader className="pb-3">
          <Calendar className="h-8 w-8 text-accent mb-2" />
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Appointments
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{appointmentStats.totalAppointments}</div>
          <p className="text-xs text-muted-foreground mt-1">
            {appointmentStats.scheduledCount} scheduled · {appointmentStats.cancelledCount} cancelled
          </p>
        </CardContent>
      </Card>

      <Card className="hover:border-accent transition-all">
        <CardHeader className="pb-3">
          <TrendingUp className="h-8 w-8 text-accent mb-2" />
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Conversion Rate
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{appointmentStats.conversionRate.toFixed(1)}%</div>
          <p className="text-xs text-muted-foreground mt-1">
            {appointmentStats.closedDeals} deals closed
          </p>
        </CardContent>
      </Card>

      <Card className="hover:border-accent transition-all">
        <CardHeader className="pb-3">
          <DollarSign className="h-8 w-8 text-accent mb-2" />
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Total Revenue
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">${quoteMetrics.totalRevenue.toFixed(0)}</div>
          <p className="text-xs text-muted-foreground mt-1">
            {quoteMetrics.totalQuotes} quotes
          </p>
        </CardContent>
      </Card>

      <Card className="hover:border-accent transition-all">
        <CardHeader className="pb-3">
          <Target className="h-8 w-8 text-accent mb-2" />
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Paid vs Pending
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-green-600">
            ${quoteMetrics.paidRevenue.toFixed(0)}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            ${quoteMetrics.pendingRevenue.toFixed(0)} pending
          </p>
        </CardContent>
      </Card>

      <Card className="hover:border-accent transition-all">
        <CardHeader className="pb-3">
          <Award className="h-8 w-8 text-accent mb-2" />
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Performance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className={`text-2xl font-bold ${performance.color}`}>
            {performance.label}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Avg: ${quoteMetrics.averageDealSize.toFixed(0)}/deal
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
